import { useMemo } from 'react'
import { ExternalLink, FileText } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import { parseFinanceProposalSnapshot } from './financeProposalSnapshotParse'

const TIPO_LABEL: Record<string, string> = {
  servico: 'Serviço',
  plano: 'Plano',
  avulso: 'Setup / avulso',
}

type Props = {
  snapshot: FinanceProposalSnapshotRow
  /** Oculta cabeçalho quando já está dentro de FinanceDetailSectionCard */
  embedded?: boolean
}

export function FinanceProposalSnapshotView({ snapshot, embedded }: Props) {
  const parsed = useMemo(() => parseFinanceProposalSnapshot(snapshot), [snapshot])

  const total =
    snapshot.totalFinal != null
      ? Number(snapshot.totalFinal)
      : parsed.meta.totalFinal ?? parsed.items.reduce((sum, i) => sum + i.total, 0)

  const pdfHref = parsed.pdfUrl ? resolvePublicAssetUrl(parsed.pdfUrl) : null
  const isPublicProposalPage = Boolean(pdfHref && /\/p\/[0-9a-f-]{36}/i.test(pdfHref))

  return (
    <section className='space-y-3'>
      {!embedded && (
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h3 className='font-semibold'>Proposta vinculada</h3>
            <p className='text-xs text-text-muted'>
              Versão {snapshot.versao}
              {snapshot.createdAt &&
                ` · ${new Date(snapshot.createdAt).toLocaleString('pt-BR')}`}
            </p>
          </div>
          {total > 0 && (
            <p className='text-right text-sm font-semibold tabular-nums text-accent'>
              {formatCurrency(total)}
            </p>
          )}
        </div>
      )}
      {embedded && total > 0 && (
        <p className='text-right text-lg font-black tabular-nums text-accent'>{formatCurrency(total)}</p>
      )}

      {pdfHref && !isPublicProposalPage && (
        <a
          href={pdfHref}
          target='_blank'
          rel='noreferrer'
          className='flex items-center gap-2 rounded-lg border border-border bg-bg-secondary p-3 text-sm text-accent hover:underline'
        >
          <FileText size={16} className='shrink-0' />
          <span className='min-w-0 flex-1 truncate'>Abrir proposta (PDF)</span>
          <ExternalLink size={14} className='shrink-0 opacity-70' />
        </a>
      )}

      {parsed.items.length > 0 && (
        <div className='rounded-lg border border-border bg-bg-secondary'>
          <p className='border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-text-muted'>
            Itens
          </p>
          <ul className='divide-y divide-border'>
            {parsed.items.map((item) => (
              <li key={item.id} className='flex items-start justify-between gap-3 px-3 py-2.5'>
                <div className='min-w-0'>
                  <p className='font-medium leading-snug'>{item.descricao}</p>
                  <p className='text-xs text-text-muted'>
                    {TIPO_LABEL[item.tipo] ?? item.tipo}
                    {item.quantidade > 1 ? ` · Qtd. ${item.quantidade}` : ''}
                  </p>
                </div>
                <span className='shrink-0 text-sm tabular-nums'>{formatCurrency(item.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(parsed.meta.desconto != null && parsed.meta.desconto > 0) ||
      parsed.meta.subtotal != null ||
      parsed.meta.validade != null ? (
        <dl className='grid gap-1.5 text-sm'>
          {parsed.meta.subtotal != null && (
            <div className='flex justify-between'>
              <dt className='text-text-muted'>Subtotal</dt>
              <dd className='tabular-nums'>{formatCurrency(parsed.meta.subtotal)}</dd>
            </div>
          )}
          {parsed.meta.desconto != null && parsed.meta.desconto > 0 && (
            <div className='flex justify-between'>
              <dt className='text-text-muted'>Desconto</dt>
              <dd className='tabular-nums'>{parsed.meta.desconto}%</dd>
            </div>
          )}
          {parsed.meta.validade != null && (
            <div className='flex justify-between'>
              <dt className='text-text-muted'>Validade</dt>
              <dd>{parsed.meta.validade} dias</dd>
            </div>
          )}
        </dl>
      ) : null}

      {parsed.meta.observacoes && (
        <div className='rounded-lg border border-border bg-bg-secondary p-3'>
          <p className='mb-1 text-xs font-semibold text-text-muted'>Observações</p>
          <p className='whitespace-pre-wrap text-sm'>{parsed.meta.observacoes}</p>
        </div>
      )}

      {parsed.items.length === 0 && !pdfHref && total <= 0 && (
        <p className='text-sm text-text-muted'>Snapshot sem detalhes adicionais.</p>
      )}
    </section>
  )
}
